import { useEffect } from 'react';
import { Search, ChevronRight, Layers, Package, BookOpen, CheckSquare, Bug } from 'lucide-react';
import { useState } from 'react';
import { useAppStore } from '../../store/useAppStore';
import { useWorkItemStore } from '../../store/useWorkItemStore';
import { useConfigStore } from '../../store/useConfigStore';
import type { WorkItem } from '../../types';

const typeIcons: Record<string, typeof Layers> = {
  epic: Layers,
  feature: Package,
  story: BookOpen,
  task: CheckSquare,
  bug: Bug,
};

export default function TasksPanel() {
  const { selectedProjectId, setDetailItemId } = useAppStore();
  const { items, fetchByProject } = useWorkItemStore();
  const { getWorkItemTypeConfig, getStatusColor } = useConfigStore();
  const [search, setSearch] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (selectedProjectId) {
      fetchByProject(selectedProjectId);
    }
  }, [selectedProjectId, fetchByProject]);

  const topLevel = items
    .filter((i) => !i.parentId)
    .filter((i) => i.title.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => a.order - b.order);

  const getChildren = (parentId: string) =>
    items.filter((i) => i.parentId === parentId).sort((a, b) => a.order - b.order);

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const renderItem = (item: WorkItem, depth: number) => {
    const children = getChildren(item._id);
    const hasChildren = children.length > 0;
    const isExpanded = expandedIds.has(item._id);
    const config = getWorkItemTypeConfig(item.workItemType);
    const Icon = typeIcons[item.workItemType] || CheckSquare;

    return (
      <div key={item._id}>
        <button
          className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-left group"
          style={{ paddingLeft: `${8 + depth * 16}px` }}
          onClick={() => setDetailItemId(item._id)}
        >
          {hasChildren ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleExpand(item._id);
              }}
              className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              <ChevronRight
                size={12}
                className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`}
              />
            </button>
          ) : (
            <span className="w-4" />
          )}

          <Icon size={14} style={{ color: config.color }} className="shrink-0" />

          {item.displayId && (
            <span className="text-[10px] font-mono text-gray-400 dark:text-gray-500 shrink-0">{item.displayId}</span>
          )}
          <span className="text-sm text-gray-800 dark:text-gray-200 truncate flex-1">{item.title}</span>

          <span
            className="w-2 h-2 rounded-full shrink-0"
            style={{ backgroundColor: getStatusColor(item.status) }}
          />
        </button>

        {hasChildren && isExpanded && (
          <div>
            {children.map((child) => renderItem(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  if (!selectedProjectId) {
    return (
      <div className="flex flex-col items-center justify-center h-full px-4 text-center text-gray-400 dark:text-gray-500">
        <p className="text-sm">Select a project first to view tasks</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="p-3">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search tasks..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-1">
        {topLevel.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-8">No work items yet</p>
        ) : (
          topLevel.map((item) => renderItem(item, 0))
        )}
      </div>
    </div>
  );
}
